import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Paper,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import GavelIcon from "@mui/icons-material/Gavel";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import StorageIcon from "@mui/icons-material/Storage";
import CodeIcon from "@mui/icons-material/Code";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { getImageUrl } from "../../../utils/helpers";

const AiAgreementsSummary = ({ baseConfig }) => {
  const { priColor, priHoverColor, tertColor, tertHoverColor } =
    baseConfig.siteBranding;

  const agreementTypes = [
    {
      id: "ai-saas-agreements",
      label: "AI SaaS Agreements",
      icon: <CloudQueueIcon sx={{ mr: 1 }} />,
      description:
        "Software as a Service agreements for AI-powered applications",
    },
    {
      id: "ai-data-agreements",
      label: "Data & Training Agreements",
      icon: <StorageIcon sx={{ mr: 1 }} />,
      description: "Contracts governing AI training data and annotation",
    },
    {
      id: "ai-model-licensing",
      label: "AI Model Licensing",
      icon: <SmartToyIcon sx={{ mr: 1 }} />,
      description: "Agreements for using or distributing AI models",
    },
    {
      id: "ai-development-agreements",
      label: "AI Development Agreements",
      icon: <CodeIcon sx={{ mr: 1 }} />,
      description: "Contracts for custom AI application development",
    },
    {
      id: "ai-consulting-agreements",
      label: "AI Consulting Agreements",
      icon: <LightbulbIcon sx={{ mr: 1 }} />,
      description: "Terms for AI strategy and implementation consulting",
    },
    {
      id: "tech-transfer",
      label: "AI Tech Transfer & IP",
      icon: <CompareArrowsIcon sx={{ mr: 1 }} />,
      description: "Technology transfer and IP assignment for AI innovations",
    },
  ];

  return (
    <Box sx={{ mb: 6 }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: `${tertColor}22`,
          borderRadius: "4px 4px 0 0",
        }}
      >
        <GavelIcon sx={{ mr: 2, color: tertColor }} />
        <Typography variant="h5" fontWeight="bold">
          AI Legal Agreements
        </Typography>
      </Box>

      {/* Main Body */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          border: `1px solid ${tertColor}33`,
          borderTop: 0,
          borderRadius: "0 0 4px 4px",
        }}
      >
        {/* Overview Section */}
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography
              variant="h6"
              fontWeight="bold"
              color={tertColor}
              gutterBottom
            >
              Comprehensive Legal Agreements for AI Applications
            </Typography>

            <Typography variant="body1" paragraph>
              Kama Thuo, PLLC specializes in drafting, reviewing, and
              negotiating a wide range of legal agreements tailored specifically
              for artificial intelligence applications and technologies. Our
              expertise ensures that your AI initiatives are properly structured
              from a legal perspective, with appropriate protections for
              intellectual property, clear definitions of responsibilities, and
              comprehensive risk management provisions.
            </Typography>

            <Typography variant="body1" paragraph>
              Whether you're developing an AI SaaS solution, licensing AI
              models, sharing training data, or engaging in AI development
              partnerships, our tailored agreements protect your interests while
              facilitating innovation and collaboration. We understand the
              unique legal challenges presented by AI technologies and
              incorporate forward-thinking provisions to address emerging
              issues.
            </Typography>

            <Typography variant="body1" paragraph>
              For a complete overview of our AI agreement services and detailed
              information about each agreement type, visit our dedicated AI
              Legal Agreements page.
            </Typography>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                endIcon={<LaunchIcon />}
                component={RouterLink}
                to="/legal-agreements-for-ai"
                sx={{
                  backgroundColor: tertColor,
                  color: "white",
                  "&:hover": {
                    color: "white",
                    backgroundColor: tertHoverColor,
                  },
                }}
              >
                Explore Our AI Agreement Services
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* AI Agreement Types Section */}
        <Divider sx={{ my: 5 }} />

        <Typography
          variant="h6"
          fontWeight="bold"
          color={tertColor}
          sx={{ mb: 2 }}
        >
          AI Agreement Types
        </Typography>

        <Grid container spacing={3}>
          {agreementTypes.map((agreement) => (
            <Grid item xs={12} sm={6} md={4} key={agreement.id}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "row",
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e0e0e0",
                  transition: "all 0.2s ease",
                  height: "100%",
                  "&:hover": {
                    backgroundColor: `${tertColor}11`,
                    borderColor: `${tertColor}66`,
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Box
                  component={RouterLink}
                  to={`/legal-agreements-for-ai#${agreement.id}`}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    textDecoration: "none",
                    color: "inherit",
                    width: "100%",
                  }}
                >
                  <Box sx={{ color: tertColor, pr: 1.5, pt: "4px" }}>
                    {agreement.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="medium">
                      {agreement.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {agreement.description}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Optional: Visual / Feature Section */}
        <Divider sx={{ my: 5 }} />

        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box
              component="img"
              src={getImageUrl("ai-agreements-banner-600x400.png")}
              alt="AI Legal Agreements"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              }}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography
              variant="h6"
              fontWeight="bold"
              color={tertColor}
              gutterBottom
            >
              Why AI-Specific Legal Agreements Matter
            </Typography>

            <Typography variant="body1" paragraph>
              Standard legal templates often fail to address the unique
              characteristics and challenges of AI technologies. Our specialized
              AI agreements incorporate provisions for:
            </Typography>

            <Grid container spacing={2}>
              {[
                {
                  title: "Data Rights & Ownership",
                  text: "Clear provisions on ownership of training data, input data, and AI-generated outputs",
                },
                {
                  title: "Model Improvements",
                  text: "Addressing rights to model improvements and learned insights from customer data",
                },
                {
                  title: "Performance Standards",
                  text: "Appropriate representations about AI performance and accuracy expectations",
                },
                {
                  title: "Regulatory Compliance",
                  text: "Terms addressing compliance with evolving AI regulations and industry standards",
                },
              ].map((item, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Box sx={{ display: "flex" }}>
                    <Box
                      sx={{
                        minWidth: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        backgroundColor: tertColor,
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1.5,
                        fontSize: "14px",
                        fontWeight: "bold",
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {item.title}
                      </Typography>
                      <Typography variant="body2">{item.text}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default AiAgreementsSummary;
